import { Button } from '@mantine/core';
import { captureMessage } from '@sentry/react';
import classNames from 'classnames/bind';
import { i18nKeys, useTranslation } from 'locales';
import qs from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { InvoiceActionsDropdown } from 'shared/components';
import CustomTableViews from 'shared/components/CustomTable/CustomTableViews';
import ExportInvoices from 'shared/components/ExportInvoices';
import { queryParamParser } from 'shared/utils/view';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';
import type { Invoice as TInvoice } from 'types/store/invoice-state';

import Invoice from '../Invoice';
import { openBatchActionsModal } from '../Listing/BatchActions';
import styleIdentifiers from './toHandle.module.scss';
import {
  type GetActionableInvoicesViewParams,
  useGetActionableInvoicesView,
} from './useGetActionableInvoicesView';

const styles = classNames.bind(styleIdentifiers);

// Builds the view-data query params from the URL: view id from the hash, filters
// from the search (parsed the same way the legacy getView saga did).
const paramsFromLocation = (hash: string, search: string): GetActionableInvoicesViewParams => ({
  view_id: hash.slice(1),
  ...queryParamParser(qs.parse(search, { arrayFormat: 'comma' })),
});

export default function InvoicesToHandle() {
  const { t } = useTranslation();
  const location = useLocation();

  const [params, setParams] = useState<GetActionableInvoicesViewParams>(() =>
    paramsFromLocation(location.hash, location.search),
  );
  const { actionableInvoices, isActionableInvoicesInitialLoading, refetchActionableInvoices } =
    useGetActionableInvoicesView(params);

  const [selectedInvoices, setSelectedInvoices] = useState<TInvoice[]>([]);
  const [areAllItemsAcrossPagesSelected, setAreAllItemsAcrossPagesSelected] = useState(false);

  // Re-sync the query params whenever the URL changes (the orchestrator writes
  // filters/sort/pagination into the URL via history.push).
  useEffect(() => {
    setParams(paramsFromLocation(location.hash, location.search));
  }, [location]);

  const showInvoice = (item: any, e: React.MouseEvent<HTMLTableRowElement>) => {
    if (e.metaKey || e.ctrlKey || e.button === 1) {
      window.open(`/invoices/${item.id}`, '_blank');
      return;
    }
    showDialog({
      id: DialogShowId.INVOICE,
      size: DialogShowSize.LARGE,
      children: <Invoice id={item.id} modal />,
    });
  };

  const reloadData = refetchActionableInvoices;

  const actionsCol = (item: unknown) => {
    if (item && typeof item === 'object' && 'actions' in item) {
      return (
        <InvoiceActionsDropdown
          shouldDeferRequest
          reload={reloadData}
          invoice={item as TInvoice}
          item={<Button size="xs">{t(i18nKeys.PROCESS)}</Button>}
        />
      );
    }

    captureMessage(
      "Tried to pass an `invoice` prop to InvoiceActionsDropdown that wasn't an object, or didn't respond to the `actions` field.",
      { extra: { item } },
    );

    return null;
  };

  const exportInvoices = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.MEDIUM,
      title: t(i18nKeys.EXPORT),
      children: <ExportInvoices resourceType="invoice" />,
    });
  };

  const openBatchActions = () => {
    openBatchActionsModal({
      title: t(i18nKeys.BATCH.TITLE),
      closeLabel: t(i18nKeys.CLOSE),
      selectedInvoices,
      isSelectedAll: areAllItemsAcrossPagesSelected,
      totalObjects: actionableInvoices?.metadata?.pagination.total_objects ?? 0,
      reload: reloadData,
    });
  };

  return (
    <CustomTableViews
      className={styles('InvoicesToHandle')}
      title={t(i18nKeys.INVOICE_TO_PROCESS)}
      tableName="actionable_invoices"
      onClickRow={showInvoice}
      callbackAction={refetchActionableInvoices}
      actions={
        selectedInvoices.length > 0 ? (
          <Button onClick={openBatchActions}>{t(i18nKeys.FORM.ACTIONS)}</Button>
        ) : (
          <Button onClick={exportInvoices}>{t(i18nKeys.EXPORT)}</Button>
        )
      }
      isLoading={isActionableInvoicesInitialLoading}
      pagination={actionableInvoices?.metadata?.pagination}
      items={actionableInvoices?.data ?? []}
      onChangeSelectionCallback={values => setSelectedInvoices(values as Array<TInvoice>)}
      onChangeSelectItemsAcrossAllPagesCallback={setAreAllItemsAcrossPagesSelected}
      noResultMessage={t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.NO_INVOICE_WITH_DELAY)}
      actionsCol={actionsCol}
    />
  );
}
